/**
 * Internacionalizacion (seccion 5.2).
 *
 * i18next con recursos estaticos `es.json` / `en.json` y claves semanticas:
 * anadir un tercer idioma es anadir un JSON y una entrada en SUPPORTED
 * (RF-LANG-08), sin tocar ningun componente.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '@/locales/en.json';
import es from '@/locales/es.json';
import type { I18nText, Language } from '@/types/domain';

export const SUPPORTED_LANGUAGES: Language[] = ['es', 'en'];

/** Idioma por defecto cuando el navegador no habla ninguno de los nuestros. */
export const FALLBACK_LANGUAGE: Language = 'en';

/** Idioma de referencia del contenido creado por el administrador (RF-LANG-06). */
export const CONTENT_DEFAULT_LANGUAGE: Language = 'es';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value as Language);
}

/**
 * Idioma preseleccionado en la pantalla de eleccion (RF-LANG-02):
 * el del navegador si es ES o EN; en cualquier otro caso, ingles.
 */
export function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return FALLBACK_LANGUAGE;
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const candidate of candidates) {
    const base = candidate?.split('-')[0]?.toLowerCase();
    if (isLanguage(base)) return base;
  }
  return FALLBACK_LANGUAGE;
}

/** Idioma del prefijo de la URL (`/es/...`), la fuente de verdad (RF-LANG-09). */
export function languageFromPath(pathname: string): Language | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  return isLanguage(segment) ? segment : null;
}

void i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es },
    en: { translation: en },
  },
  lng: languageFromPath(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  ) ?? detectBrowserLanguage(),
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: {
    // React ya escapa; volver a escapar romperia acentos y comillas.
    escapeValue: false,
  },
  returnEmptyString: false,
  react: { useSuspense: false },
});

export default i18n;

/**
 * Resuelve un texto multilingue del administrador (RF-LANG-06).
 * Si falta la traduccion se cae al idioma por defecto y se avisa con una
 * marca discreta, en lugar de dejar el hueco vacio.
 */
export function resolveI18nText(
  text: I18nText | null | undefined,
  language: Language,
): { value: string; untranslated: boolean } {
  if (!text) return { value: '', untranslated: false };

  const direct = text[language]?.trim();
  if (direct) return { value: direct, untranslated: false };

  const fallback = text[CONTENT_DEFAULT_LANGUAGE]?.trim();
  if (fallback) return { value: fallback, untranslated: true };

  // Ultimo recurso: cualquier idioma con contenido.
  const any = Object.values(text).find((v) => v?.trim());
  return { value: any ?? '', untranslated: Boolean(any) };
}

/** Sustituye el prefijo de idioma de una ruta conservando el resto. */
export function pathWithLanguage(pathname: string, language: Language): string {
  const segments = pathname.split('/').filter(Boolean);
  if (isLanguage(segments[0])) segments[0] = language;
  else segments.unshift(language);
  return `/${segments.join('/')}`;
}
